import AuthService from "@/core/services/Auth.service";
import type { IStudioSessionData } from "@/core/entities/session/StudioSession";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";
import { sessionRepository } from "@/infrastructure/repositories/session/Session.repository";

interface SessionApiResponse {
  data: IStudioSessionData;
  message: string;
}

interface SessionApiError {
  status: string;
  message: string;
  code?: string;
}

export type BootstrapSessionResult =
  | { ok: true; data: IStudioSessionData }
  | { ok: false; code?: string; message: string };

let inFlight: Promise<BootstrapSessionResult> | null = null;
let lastSuccessfulBootstrapMs = 0;
let lastFailure: { at: number; result: BootstrapSessionResult } | null = null;

const FAILURE_COOLDOWN_MS = 30_000;
const SESSION_RETRY_ATTEMPTS = 3;
const SESSION_RETRY_DELAY_MS = 800;

const NON_RETRYABLE_CODES = new Set([
  "USER_NOT_REGISTERED",
  "UNAUTHORIZED",
  "AUTH0_TOKEN_ERROR",
  "AUTH0_TOKEN_UNAVAILABLE",
  "AUTH_NOT_CONFIGURED",
]);

export function getLastSuccessfulBootstrapMs(): number {
  return lastSuccessfulBootstrapMs;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isRetryableFailure(result: BootstrapSessionResult): boolean {
  if (result.ok) {
    return false;
  }

  if (result.code && NON_RETRYABLE_CODES.has(result.code)) {
    return false;
  }

  return (
    result.code === "INTERNAL_ERROR" ||
    result.code === "SESSION_REQUEST_FAILED" ||
    result.message.includes("fetch failed") ||
    result.message === "Session bootstrap failed"
  );
}

async function requestStudioSession(): Promise<BootstrapSessionResult> {
  const config = WebAppConfig.getInstance();

  let token: string | null;

  try {
    token = await AuthService.getInstance().getUserAccessToken();
  } catch {
    return {
      ok: false,
      code: "AUTH0_TOKEN_ERROR",
      message: "Your authentication session has expired. Please sign in again.",
    };
  }

  if (!token) {
    return {
      ok: false,
      code: "AUTH0_TOKEN_UNAVAILABLE",
      message: "Unable to retrieve authentication token",
    };
  }

  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/session`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      let errorBody: SessionApiError | null = null;
      try {
        errorBody = (await response.json()) as SessionApiError;
      } catch {
        // ignore JSON parse errors
      }

      return {
        ok: false,
        code: errorBody?.code,
        message: errorBody?.message ?? "Session bootstrap failed",
      };
    }

    const body = (await response.json()) as SessionApiResponse;
    return { ok: true, data: body.data };
  } catch (error) {
    return {
      ok: false,
      code: "SESSION_REQUEST_FAILED",
      message: error instanceof Error ? error.message : "Session bootstrap failed",
    };
  }
}

async function requestStudioSessionWithRetry(): Promise<BootstrapSessionResult> {
  let lastResult: BootstrapSessionResult = { ok: false, message: "Session bootstrap failed" };

  for (let attempt = 1; attempt <= SESSION_RETRY_ATTEMPTS; attempt++) {
    lastResult = await requestStudioSession();

    if (lastResult.ok || !isRetryableFailure(lastResult)) {
      return lastResult;
    }

    if (attempt < SESSION_RETRY_ATTEMPTS) {
      await delay(SESSION_RETRY_DELAY_MS * attempt);
    }
  }

  return lastResult;
}

/**
 * Ensures a studio session exists, deduplicating concurrent calls via a
 * shared in-flight promise and backing off for 30 s after all retries fail.
 *
 * Pass `{ force: true }` to skip the reusable-session check and failure
 * cooldown (e.g. on 401 recovery or periodic keep-alive).
 */
export async function ensureStudioSession(
  options: { force?: boolean } = {},
): Promise<BootstrapSessionResult> {
  if (inFlight !== null) {
    return inFlight;
  }

  if (!options.force && sessionRepository.hasReusableSession()) {
    const data = sessionRepository.getData();
    if (data) {
      return { ok: true, data };
    }
  }

  if (
    !options.force &&
    lastFailure &&
    Date.now() - lastFailure.at < FAILURE_COOLDOWN_MS
  ) {
    return lastFailure.result;
  }

  inFlight = (async () => {
    const result = await requestStudioSessionWithRetry();
    if (result.ok) {
      sessionRepository.save(result.data);
      lastSuccessfulBootstrapMs = Date.now();
      lastFailure = null;
    } else {
      lastFailure = { at: Date.now(), result };
    }
    return result;
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}
